import { useState } from "react";
import type { Order, PrintNotice } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { Dialog } from "../../ui/Dialog";
import { reprintToast } from "../printing/print-notices";
import { printAgain } from "../printing/reprint";
import { Receipt, routeUrl } from "./Receipt";

interface Props {
  order: Order;
  /** Where it was just printed (after Bestellen, or 🖨 Afdrukken): "Afgedrukt op Kassaprinter toog · 80 mm papier". */
  printed?: PrintNotice | undefined;
  onClose: () => void;
}

/** The paper of this device's receipts: a Sunmi prints on its own 58 mm printer (prototype receiptTarget). */
export function receiptPaper(deviceType: string): 58 | 80 {
  return deviceType === "sunmi" ? 58 : 80;
}

/**
 * "Kassabon" (prototype openReceipt): right after a delivery is ordered, or 🖨 Afdrukken for one order. The
 * receipt with the route QR for the courier, and where it was printed. "Afdrukken" prints it again on the
 * receipt printer; a shop without one prints from the browser.
 */
export function ReceiptDialog({ order, printed, onClose }: Props) {
  const { api, status, general, toast, report } = useTill();
  const [busy, setBusy] = useState(false);
  const text = t.receiptDialog;
  const paper = printed?.paper ?? receiptPaper(status.device.type);

  const print = async () => {
    setBusy(true);
    try {
      const prints = await printAgain(api, [order.id]);
      const said = reprintToast(prints, order.code);
      if (said === null) window.print();
      else toast(said);
    } catch (error) {
      report(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={text.title}
      onClose={onClose}
      footer={
        <>
          <span className="spacer" />
          <button type="button" className="btn" disabled={busy} onClick={() => void print()}>
            🖨 {t.orders.print}
          </button>
          <button type="button" className="btn primary" data-autofocus onClick={onClose}>
            OK
          </button>
        </>
      }
    >
      {printed !== undefined && (
        // prototype: "Afgedrukt op Kassaprinter toog · 80 mm papier. De breedte volgt …"
        <p className="note printedon">
          {t.print.printedOn[0]} <b>{printed.printer}</b>
          {t.print.printedOn[1](printed.paper)}
        </p>
      )}
      <div className="bonwrap zbonpanel">
        <Receipt order={order} shop={status.shop} general={general} paper={paper} />
      </div>
      {order.channel === "delivery" && order.customer.street && (
        <p className="note">
          {text.qr}{" "}
          <a href={routeUrl(order.customer)} target="_blank" rel="noopener noreferrer">
            {text.testLink}
          </a>
          .
        </p>
      )}
      <p className="note">{text.setup}</p>
    </Dialog>
  );
}
